import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Utils from "../util/Utils";

export const PERF_METRICS = [
  { key: "perfDay", label: "Performance (1 day)" },
  { key: "perfWeek", label: "Performance (1 week)" },
  { key: "perfMonth", label: "Performance (1 month)" },
  { key: "perfYTD", label: "Performance (YTD)" },
  { key: "perfYear", label: "Performance (1 year)" },
  { key: "perf5Year", label: "5Y (Annualized)" },
  { key: "perf5YearTotal", label: "5Y (Actual return)" },
  { key: "perf10Year", label: "10Y (Annualized)" },
  { key: "perf10YearTotal", label: "10Y (Actual return)" },
];

export function parsePerfNumber(v) {
  return Utils.parsePerfNumber(v);
}

function gradientIdPrefix(chartKey) {
  return String(chartKey).replace(/[^a-zA-Z0-9_-]/g, "_");
}

function barGradientFill(prefix, value) {
  const id = gradientIdPrefix(prefix);
  if (value > 0) return `url(#${id}_pos)`;
  if (value < 0) return `url(#${id}_neg)`;
  return "#9e9e9e";
}

function chartBarGradientDefs(chartKey) {
  const id = gradientIdPrefix(chartKey);
  return (
    <defs>
      <linearGradient id={`${id}_pos`} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#145523" stopOpacity={1} />
        <stop offset="40%" stopColor="#2e7d32" stopOpacity={1} />
        <stop offset="100%" stopColor="#8fd99a" stopOpacity={0.88} />
      </linearGradient>
      <linearGradient id={`${id}_neg`} x1="1" y1="0" x2="0" y2="0">
        <stop offset="0%" stopColor="#3d0f0f" stopOpacity={1} />
        <stop offset="35%" stopColor="#8b1c1c" stopOpacity={1} />
        <stop offset="100%" stopColor="#ff7961" stopOpacity={0.9} />
      </linearGradient>
    </defs>
  );
}

const BAR_ANIMATION = {
  isAnimationActive: true,
  animationDuration: 520,
  animationEasing: "ease-out",
};

export function buildPerfChartData(stockReports, metricKey) {
  if (!Array.isArray(stockReports) || !stockReports.length) return [];
  const rows = stockReports
    .map((r) => {
      const v = parsePerfNumber(r[metricKey]);
      if (v == null) return null;
      const sym = r.symbol || "";
      const name = r.companyName || sym;
      return {
        label: sym || name,
        fullName: name,
        value: v,
      };
    })
    .filter(Boolean);
  rows.sort((a, b) => b.value - a.value);
  return rows;
}

function chartMinHeight(count) {
  if (count <= 0) return 72;
  const perRow = 14;
  const chrome = 40;
  return Math.max(80, count * perRow + chrome);
}

function yAxisWidthForLabels(data, minW, maxW) {
  if (!data.length) return minW;
  const maxLen = Math.max(3, ...data.map((d) => String(d.label).length));
  return Math.min(maxW, Math.max(minW, 8 + maxLen * 5.5));
}

const CHART_MARGIN_COMPACT = { top: 2, right: 4, left: 2, bottom: 2 };
const AXIS_TICK_COMPACT = { fontSize: 10, fill: "#424242" };
const LABEL_LIST_STYLE = { fill: "#37474f", fontSize: 10, fontWeight: 500 };
/** Inside negative (red) bars — light fill + thin dark stroke for contrast on gradients */
const LABEL_LIST_NEGATIVE_SX = {
  fill: "#fffefb",
  fontSize: 10,
  fontWeight: 600,
  stroke: "rgba(55, 12, 12, 0.92)",
  strokeWidth: 2,
  paintOrder: "stroke fill",
  strokeLinejoin: "round",
};

/** Bold category labels (symbols); optional click to open profile. */
function BoldCategoryTick({ x, y, payload, onSymbolClick }) {
  if (!payload) return null;
  const v = String(payload.value ?? "");
  return (
    <text
      x={x}
      y={y}
      dy={4}
      textAnchor="end"
      fill="#424242"
      fontSize={10}
      fontWeight={700}
      style={{ cursor: onSymbolClick ? "pointer" : undefined }}
      onClick={
        onSymbolClick
          ? (e) => {
              e.stopPropagation();
              onSymbolClick(v);
            }
          : undefined
      }
    >
      {v}
    </text>
  );
}

const CHART_OUTER_SX = {
  width: { xs: "100%", sm: "50%" },
  maxWidth: "100%",
  mx: "auto",
  overflow: "visible",
  flexShrink: 0,
};

/** Subtle frame so charts read as a single unit on busy layouts */
const CHART_FRAME_SX = {
  border: "1px solid",
  borderColor: "rgba(15, 45, 82, 0.07)",
  borderRadius: 1,
  boxSizing: "border-box",
};

function formatBarValueLabel(v) {
  if (v == null || !Number.isFinite(Number(v))) return "";
  return `${Number(v).toFixed(2)}%`;
}

/**
 * Keeps % labels off the Y-axis: for negative horizontal bars, default LabelList "right"
 * sits on the far-left tip and overlaps symbols. Anchor labels toward x=0 (inside bar).
 */
function PerfBarValueLabel(props) {
  const { x, y, width, height, value, payload } = props;
  const v = Number(
    value !== undefined && value !== null ? value : payload?.value
  );
  if (!Number.isFinite(v)) return null;
  const text = formatBarValueLabel(v);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  const h = Number(height) || 0;
  const midY = y + h / 2;
  const w = Number(width) || 0;
  const left = Math.min(x, x + w);
  const right = Math.max(x, x + w);
  const style = LABEL_LIST_STYLE;
  if (v >= 0) {
    return (
      <text
        x={right + 6}
        y={midY}
        dominantBaseline="middle"
        textAnchor="start"
        fill={style.fill}
        fontSize={style.fontSize}
        fontWeight={style.fontWeight}
      >
        {text}
      </text>
    );
  }
  return (
    <text
      x={right - 6}
      y={midY}
      dominantBaseline="middle"
      textAnchor="end"
      fill={LABEL_LIST_NEGATIVE_SX.fill}
      fontSize={LABEL_LIST_NEGATIVE_SX.fontSize}
      fontWeight={LABEL_LIST_NEGATIVE_SX.fontWeight}
      stroke={LABEL_LIST_NEGATIVE_SX.stroke}
      strokeWidth={LABEL_LIST_NEGATIVE_SX.strokeWidth}
      paintOrder={LABEL_LIST_NEGATIVE_SX.paintOrder}
      strokeLinejoin={LABEL_LIST_NEGATIVE_SX.strokeLinejoin}
    >
      {text}
    </text>
  );
}

function PerfTooltip({ metricLabel, active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        p: 1,
        border: 1,
        borderColor: "divider",
        boxShadow: 1,
      }}
    >
      <Typography variant="body2" fontWeight={600}>
        {p.fullName}
      </Typography>
      <Typography variant="body2">
        {metricLabel}: {p.value.toFixed(2)}%
      </Typography>
    </Box>
  );
}

/**
 * @param {object} props
 * @param {string} props.chartKey Unique id prefix for SVG gradients
 * @param {(symbol: string) => void} [props.onSymbolClick] Opens company/ETF profile (e.g. StockScreener modal pattern)
 * @param {boolean} [props.fillContainer] If true, chart uses 100% of parent width (e.g. multi-column grid). Default keeps compact centered layout.
 */
export function HorizontalPerfBarChart({
  data,
  metricLabel,
  chartKey,
  onSymbolClick,
  fillContainer = false,
}) {
  const h = chartMinHeight(data.length);
  const yw = Math.round(yAxisWidthForLabels(data, 56, 160));
  const labelReserve = 52;
  const gradientDefs = chartBarGradientDefs(chartKey);
  const tickEl = (tickProps) => (
    <BoldCategoryTick {...tickProps} onSymbolClick={onSymbolClick} />
  );
  const outerSx = fillContainer
    ? {
        width: "100%",
        maxWidth: "100%",
        mx: 0,
        overflow: "visible",
        flexShrink: 0,
      }
    : CHART_OUTER_SX;
  return (
    <Box sx={{ ...outerSx, ...CHART_FRAME_SX, height: h, mb: 1 }}>
      <ResponsiveContainer width="100%" height={h}>
        <BarChart
          layout="vertical"
          data={data}
          margin={{
            ...CHART_MARGIN_COMPACT,
            right: CHART_MARGIN_COMPACT.right + labelReserve,
          }}
          barCategoryGap={0}
          barGap={0}
        >
          {gradientDefs}
          <CartesianGrid
            strokeDasharray="4 4"
            stroke="#e8e8e8"
            strokeOpacity={0.85}
            vertical
            horizontal={false}
          />
          <XAxis
            type="number"
            tickFormatter={(v) => `${v}`}
            tick={AXIS_TICK_COMPACT}
            axisLine={{ stroke: "#cfd8dc" }}
            tickLine={false}
            height={20}
          />
          <YAxis
            type="category"
            dataKey="label"
            width={yw}
            tick={tickEl}
            tickLine={false}
            axisLine={false}
            interval={0}
            padding={{ top: 2, bottom: 2 }}
          />
          <Tooltip
            content={(tooltipProps) => (
              <PerfTooltip {...tooltipProps} metricLabel={metricLabel} />
            )}
            cursor={{ fill: "rgba(33, 150, 243, 0.06)" }}
          />
          <ReferenceLine x={0} stroke="#b0bec5" strokeWidth={1} />
          <Bar
            dataKey="value"
            radius={[0, 3, 3, 0]}
            cursor={{ fill: "rgba(33, 150, 243, 0.06)" }}
            {...BAR_ANIMATION}
          >
            <LabelList
              dataKey="value"
              content={(labelProps) => <PerfBarValueLabel {...labelProps} />}
            />
            {data.map((entry, i) => (
              <Cell
                key={`${chartKey}-${entry.label}-${i}`}
                fill={barGradientFill(chartKey, entry.value)}
                style={{ cursor: onSymbolClick ? "pointer" : undefined }}
                onClick={
                  onSymbolClick
                    ? () => onSymbolClick(String(entry.label))
                    : undefined
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
